import { useReducer } from 'react';

import { linkpassPost } from '../../../utils/fetch';
import lineBreaker from '../../../utils/lineBreaker';
import { stripErrorCode } from '../../../utils/linkpassAdapters';

import categorizeRates from './categorizeRates';
import { exchangeConversionReducer, initialState } from './exchangeConversionReducer';

const GENERIC_FAILURE = 'Something went wrong. Please try again.';
const SESSION_EXPIRED = 'Your session expired. Please log in again.';
const TIMEOUT_FAILURE =
  'This is taking longer than expected. Please check your balance before trying again, in case the exchange went through.';

/*
 * The partner is credited inside this request, so it is legitimately slow - but not
 * minutes. Cut it off well before the browser default so the member gets an answer, and
 * word that answer carefully: a timeout means we do not know whether it completed.
 */
const CONVERSION_TIMEOUT_MS = 45000;

const useExchangeConversionState = ({ partnerData }) => {
  const {
    id,
    rates,
    config: { merchantExchangeUrl },
  } = partnerData;

  const [state, dispatch] = useReducer(exchangeConversionReducer, initialState);
  const { balances, selectedRate, modal } = state;

  /*
   * balances stays empty until a conversion writes to it, so fall back to the figures the
   * page was rendered with - index.js already does this for the displayed totals. Without
   * the same fallback here categorizeRates compares against undefined, and `100 > NaN` is
   * false, so every rate stayed selectable no matter how few points the member had.
   */
  const effectiveBalances = {
    availableLpBalance: balances.availableLpBalance ?? partnerData.availableLpBalance,
    pointExcMerchantBal: balances.pointExcMerchantBal ?? partnerData.pointExcMerchantBal,
  };

  const groupedRates = categorizeRates(rates, effectiveBalances);
  const partnerPointsName = groupedRates.out.length > 0 && groupedRates.out[0].destinationUnit;

  /*
   * Adjust the figures on screen by what was just converted. Neither API returns the new
   * balances, so this keeps the display honest until the reload on modal dismiss.
   */
  const applySuccess = () => {
    const outbound = selectedRate.sourceCode === 'LP';
    const lpBalanceAdjustment = outbound
      ? -Number(selectedRate.source)
      : Number(selectedRate.destination);
    const partnerBalanceAdjustment = outbound
      ? Number(selectedRate.destination)
      : -Number(selectedRate.source);

    dispatch({
      type: 'SET_BALANCES',
      payload: {
        availableLpBalance: Number(partnerData.availableLpBalance) + lpBalanceAdjustment,
        pointExcMerchantBal: Number(partnerData.pointExcMerchantBal) + partnerBalanceAdjustment,
      },
    });
    dispatch({
      type: 'SET_MODAL_TEXT',
      payload: {
        title: 'Thank you for using Linkpoints Exchange!',
        body: 'Your transaction has been successfully processed.',
      },
    });
    dispatch({ type: 'SET_MODAL_VISIBLE', payload: true });
    dispatch({ type: 'SET_MODAL_CLOSE_ACTION', payload: 'reload' });
  };

  const showFailure = ({ text, isHtml }) => {
    dispatch({
      type: 'SET_MODAL_TEXT',
      payload: { title: 'Exchange unsuccessful', body: text, bodyIsHtml: isHtml },
    });
    dispatch({ type: 'SET_MODAL_VISIBLE', payload: true });
  };

  const resetSelection = (btn) => {
    dispatch({ type: 'SET_SELECTED_RATE', payload: null });
    btn.disabled = false;
    btn.classList.remove('loading');
    const checked = document.querySelector('.radio-input:checked');
    if (checked) {
      checked.checked = false;
    }
  };

  /*
   * Picks the member-facing copy for a failed conversion. Always { text, isHtml } so the
   * dialog has one shape to render: the server's message carries an anchor to the partner's
   * support page and has to survive as markup, while our own copy must not be trusted as
   * HTML just because it travels the same path.
   */
  const conversionFailureMessage = (err) => {
    if (err.timeout) {
      return { text: TIMEOUT_FAILURE, isHtml: false };
    }
    if (err.status === 401) {
      dispatch({ type: 'SET_MODAL_CLOSE_ACTION', payload: 'reload' });
      return { text: SESSION_EXPIRED, isHtml: false };
    }

    const message = stripErrorCode(err.serverMessage);
    return message
      ? { text: lineBreaker(message), isHtml: true }
      : { text: GENERIC_FAILURE, isHtml: false };
  };

  /*
   * Linkpass: POST /pex/me/exchange-points takes the rate rather than the old
   * partnerIdentity / txnByPartnerWebsite pair, and signals the outcome with the HTTP
   * status - a 2xx returns { kfTransactionId, linkTransactionId } and a failure returns
   * { code, status, message } where message is copy written for the member.
   */
  const convertViaLinkpass = async (btn) => {
    dispatch({ type: 'SET_CONVERTING', payload: true });
    try {
      await linkpassPost(
        '/pex/me/exchange-points',
        {
          exchangeId: id,
          tierId: selectedRate.id,
          sourceCode: selectedRate.sourceCode,
          destinationCode: selectedRate.destinationCode,
        },
        undefined,
        // this flow explains an expired session in its own modal, see conversionFailureMessage
        { timeoutMs: CONVERSION_TIMEOUT_MS, redirectOnExpiry: false },
      );
      applySuccess();
    } catch (err) {
      /*
       * Without this the only trace of a failed conversion is the modal copy, which for a
       * 500 with no body is just "something went wrong" - nothing to diagnose from.
       */
      // eslint-disable-next-line no-console
      console.error('exchange-points failed', {
        status: err.status,
        timeout: Boolean(err.timeout),
        exchangeId: id,
        tierId: selectedRate.id,
        message: err.serverMessage || err.message,
      });

      showFailure(conversionFailureMessage(err));
    } finally {
      dispatch({ type: 'SET_CONVERTING', payload: false });
      resetSelection(btn);
    }
  };

  async function fetchConvert(e) {
    const btn = e.target;
    btn.disabled = true;
    btn.classList.add('loading');
    dispatch({ type: 'SET_MODAL_CLOSE_ACTION', payload: null });
    e.preventDefault();

    await convertViaLinkpass(btn);
  }

  const handleDirectionToggle = () => {
    dispatch({
      type: 'TOGGLE_DIRECTION',
      selectedRate: null,
    });
  };

  const handleDismiss = () => {
    dispatch({ type: 'SET_MODAL_VISIBLE', payload: false });
    if (modal.closeAction === 'reload') {
      window.location.reload();
    }
  };

  const isConversionType = () => {
    if (!merchantExchangeUrl) {
      return state.direction === 'in' || state.direction === 'out';
    }
    return state.direction === 'out';
  };

  return {
    state,
    dispatch,
    groupedRates,
    partnerPointsName,
    fetchConvert,
    handleDismiss,
    isConversionType,
    handleDirectionToggle,
  };
};

export default useExchangeConversionState;
